// src/features/dashboard/pages/EmployeeDashboard.tsx

import {
  useEffect,
  useRef,
  useState,
} from 'react';

import DashboardHeader from '../components/DashboardHeader';

import EmployeeStatsSection from '../components/EmployeeStatsSection';

import ActivityTimeline from '../components/ActivityTimeline';

import WeeklyHoursChart from '../components/WeeklyHoursChart';

import MonthlyProductivity from '../components/MonthlyProductivity';

import TopApplications from '../components/TopApplications';

import OverviewCard from '../components/OverviewCard';

import MonthlyHoursTrend from '../components/EmployeeLoggedHoursReport/MonthlyHoursTrend';

import {
  getEmployeeName,
} from '../../../features/auth/utils/Login/authStorage';

import {
  useEmployeeLoggedHours,
} from '../hooks/Employee/useEmployeeLoggedHours';


/* ============================================================================
 * LOADER
 * ========================================================================== */

const DashboardLoader = () => {

  return (
    <div
      className="
        flex
        min-h-[520px]
        w-full
        min-w-0
        flex-col
        items-center
        justify-center
        overflow-hidden
        rounded-[12px]
        bg-[#F8F9FB]
      "
    >

      {/* ================================================================
          SPINNER
      ================================================================ */}

      <div
        className="
          h-[38px]
          w-[38px]
          shrink-0
          animate-spin
          rounded-full
          border-[4px]
          border-[#E4E7EC]
          border-t-[#2563EB]
        "
      />


      {/* ================================================================
          LOADING TEXT
      ================================================================ */}

      <p
        className="
          mt-[12px]
          whitespace-nowrap
          text-[13px]
          font-medium
          text-[#475467]
        "
      >
        Loading dashboard...
      </p>


      <p
        className="
          mt-[4px]
          whitespace-nowrap
          text-[11px]
          text-[#98A2B3]
        "
      >
        Fetching your latest activity
      </p>

    </div>
  );
};


/* ============================================================================
 * EMPLOYEE DASHBOARD
 * ========================================================================== */

const EmployeeDashboard = () => {

  const employeeName =
    getEmployeeName();


  /* ==========================================================================
   * LOGGED HOURS / MONTHLY TREND
   * ======================================================================== */

  const {
    trend30Day,
    loading,
  } = useEmployeeLoggedHours();


  /* ==========================================================================
   * CURRENT LIVE TIME
   * ======================================================================== */

  const [
    currentTime,
    setCurrentTime,
  ] = useState(
    new Date(),
  );


  useEffect(() => {

    const timer =
      window.setInterval(
        () => {

          setCurrentTime(
            new Date(),
          );

        },
        1000,
      );


    return () => {

      window.clearInterval(
        timer,
      );

    };

  }, []);


  /* ==========================================================================
   * CURRENT DATE
   * ======================================================================== */

  const formattedDate =
    new Intl.DateTimeFormat(
      'en-IN',
      {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone:
          'Asia/Kolkata',
      },
    )
      .format(
        currentTime,
      )
      .toUpperCase();


  /* ==========================================================================
   * CURRENT TIME
   * ======================================================================== */

  const formattedTime =
    new Intl.DateTimeFormat(
      'en-IN',
      {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone:
          'Asia/Kolkata',
      },
    ).format(
      currentTime,
    );


  /* ==========================================================================
   * CURRENT HOUR
   * ======================================================================== */

  const hour =
    Number(
      new Intl.DateTimeFormat(
        'en-IN',
        {
          hour: '2-digit',
          hour12: false,
          timeZone:
            'Asia/Kolkata',
        },
      ).format(
        currentTime,
      ),
    );


  /* ==========================================================================
   * GREETING
   * ======================================================================== */

  const greeting =
    hour < 12
      ? 'Good morning'
      : hour < 17
        ? 'Good afternoon'
        : hour < 21
          ? 'Good evening'
          : 'Good night';


  /* ==========================================================================
   * BROWSER ZOOM DETECTION
   *
   * IMPORTANT:
   *
   * Chrome browser zoom changes:
   *
   *      80%
   *       ↓
   *     100%
   *       ↓
   *     125%
   *
   * This changes window.devicePixelRatio.
   *
   * Some dashboard/chart components calculate their width/height when they
   * initially mount. Therefore after browser zoom changes, the browser can
   * display an old layout until the page is refreshed.
   *
   * This code automatically refreshes the page when the browser zoom changes.
   *
   * It does NOT refresh continuously.
   *
   * It refreshes only when devicePixelRatio changes.
   * ======================================================================== */

  const initialDevicePixelRatio =
    useRef(
      window.devicePixelRatio,
    );


  const zoomReloadInProgress =
    useRef(false);


  useEffect(() => {

    let resizeTimer:
      number | undefined;


    const checkBrowserZoom =
      () => {

        const currentDevicePixelRatio =
          window.devicePixelRatio;


        const previousDevicePixelRatio =
          initialDevicePixelRatio.current;


        /* ================================================================
           ZOOM HAS CHANGED
        ================================================================ */

        if (
          currentDevicePixelRatio !==
          previousDevicePixelRatio
        ) {

          /*
           * Prevent multiple reload calls because Chrome can fire several
           * resize events during one zoom operation.
           */

          if (
            zoomReloadInProgress.current
          ) {

            return;

          }


          zoomReloadInProgress.current =
            true;


          console.log(
            '================================================',
          );

          console.log(
            'BROWSER ZOOM CHANGED',
          );

          console.log(
            'Previous devicePixelRatio:',
            previousDevicePixelRatio,
          );

          console.log(
            'Current devicePixelRatio:',
            currentDevicePixelRatio,
          );

          console.log(
            'Refreshing dashboard...',
          );

          console.log(
            '================================================',
          );


          /*
           * Small delay allows Chrome to finish applying the new zoom
           * before reloading the page.
           */

          window.setTimeout(
            () => {

              window.location.reload();

            },
            100,
          );

        }

      };


    /* ================================================================
       NORMAL WINDOW RESIZE
    ================================================================ */

    const handleResize =
      () => {

        if (
          resizeTimer !== undefined
        ) {

          window.clearTimeout(
            resizeTimer,
          );

        }


        resizeTimer =
          window.setTimeout(
            () => {

              checkBrowserZoom();

            },
            100,
          );

      };


    /* ================================================================
       WINDOW RESIZE LISTENER
    ================================================================ */

    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true,
      },
    );


    /* ================================================================
       VISUAL VIEWPORT RESIZE
    ================================================================ */

    const visualViewport =
      window.visualViewport;


    if (
      visualViewport
    ) {

      visualViewport.addEventListener(
        'resize',
        handleResize,
      );

    }


    /* ================================================================
       CLEANUP
    ================================================================ */

    return () => {

      window.removeEventListener(
        'resize',
        handleResize,
      );


      if (
        visualViewport
      ) {

        visualViewport.removeEventListener(
          'resize',
          handleResize,
        );

      }


      if (
        resizeTimer !== undefined
      ) {

        window.clearTimeout(
          resizeTimer,
        );

      }

    };

  }, []);


  /* ==========================================================================
   * PAGE
   * ======================================================================== */

  return (
    <>
      {/* ======================================================================
          RESPONSIVE DASHBOARD CSS
      ====================================================================== */}

      <style>
        {`

          /* ================================================================
             GLOBAL DASHBOARD SAFETY
          ================================================================ */

          .employee-dashboard-page {
            width: 100%;
            min-width: 0;
            min-height: 100vh;
            overflow-x: hidden;
            box-sizing: border-box;
          }


          .employee-dashboard-page *,
          .employee-dashboard-page *::before,
          .employee-dashboard-page *::after {
            box-sizing: border-box;
          }


          /* ================================================================
             DASHBOARD CONTENT
          ================================================================ */

          .employee-dashboard-content {
            width: 100%;
            min-width: 0;
            max-width: 100%;
          }


          /* ================================================================
             RESPONSIVE GRID
          ================================================================ */

          .employee-dashboard-grid {
            display: grid;
            width: 100%;
            min-width: 0;
            gap: 14px;
          }


          .employee-dashboard-grid-item {
            width: 100%;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
          }


          /* ================================================================
             DAILY ACTIVITY + 30 DAY TREND
          ================================================================ */

          .employee-dashboard-two-column {
            grid-template-columns:
              minmax(0, 1fr)
              minmax(0, 1.35fr);
          }


          /* ================================================================
             WEEKLY HOURS + MONTHLY PRODUCTIVITY
          ================================================================ */

          .employee-dashboard-weekly-grid {
            grid-template-columns:
              minmax(0, 1.42fr)
              minmax(0, 1fr);
          }


          /* ================================================================
             TOP APPLICATIONS + OVERVIEW
          ================================================================ */

          .employee-dashboard-app-grid {
            grid-template-columns:
              minmax(0, 1.6fr)
              minmax(0, 1fr);
          }


          /* ================================================================
             LIVE STATUS
          ================================================================ */

          .employee-dashboard-live-status {
            display: flex;
            min-width: 0;
            max-width: 100%;
            align-items: center;
            flex-wrap: wrap;
            gap: 6px;
          }


          .employee-dashboard-live-text {
            white-space: nowrap;
          }


          /* ================================================================
             TEXT SAFETY
          ================================================================ */

          .employee-dashboard-page h1,
          .employee-dashboard-page h2,
          .employee-dashboard-page h3,
          .employee-dashboard-page h4,
          .employee-dashboard-page p {
            max-width: 100%;
          }


          /* ================================================================
             CHART / CARD SAFETY
          ================================================================ */

          .employee-dashboard-chart-container {
            display: block;
            width: 100%;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
          }


          /* ================================================================
             REAL SMALL VIEWPORT
          ================================================================ */

          @media (max-width: 799px) {

            .employee-dashboard-two-column {
              grid-template-columns:
                minmax(0, 1fr) !important;
            }


            .employee-dashboard-weekly-grid {
              grid-template-columns:
                minmax(0, 1fr) !important;
            }


            .employee-dashboard-app-grid {
              grid-template-columns:
                minmax(0, 1fr) !important;
            }


            .employee-dashboard-main-container {
              padding-left: 12px !important;
              padding-right: 12px !important;
              padding-top: 12px !important;
              padding-bottom: 12px !important;
            }


            .employee-dashboard-grid {
              gap: 12px;
            }

          }

        `}
      </style>


      {/* ======================================================================
          DASHBOARD PAGE
      ====================================================================== */}

      <div
        className="
          employee-dashboard-page

          min-h-screen
          w-full
          min-w-0

          overflow-x-hidden

          bg-[#F4F5F7]

          px-[6px]
          pb-[8px]
          pt-0
        "
      >

        {/* ====================================================================
            DASHBOARD HEADER
        ==================================================================== */}

        <DashboardHeader
          breadcrumbs={[
            {
              label:
                'Work Vista',

              path:
                '/',
            },

            {
              label:
                'Employee Dashboard',
            },
          ]}
        />


        {/* ====================================================================
            MAIN DASHBOARD CONTAINER
        ==================================================================== */}

        <div
          className="
            employee-dashboard-main-container
            employee-dashboard-content

            -mt-[14px]

            w-full
            min-w-0
            max-w-full

            overflow-hidden

            rounded-[16px]

            border
            border-[#D0D5DD]

            bg-[#F8F9FB]

            px-[16px]
            py-[14px]
          "
        >

          {/* ================================================================
              GREETING
          ================================================================ */}

          <div
            className="
              mb-[14px]

              flex
              min-w-0
              w-full

              flex-wrap
              items-center

              gap-[8px]
            "
          >

            {/* Employee Greeting */}

            <h2
              className="
                shrink-0
                whitespace-nowrap

                text-[14px]
                font-semibold
                leading-[20px]

                text-[#002B5B]
              "
            >
              {greeting},{' '}
              {employeeName}
            </h2>


            {/* Live Status */}

            <div
              className="
                employee-dashboard-live-status

                rounded-full

                bg-[#EEF6FF]

                px-[10px]
                py-[7px]

                text-[10px]
                font-medium
              "
            >

              {/* Live Indicator */}

              <span
                className="
                  h-[10px]
                  w-[10px]
                  shrink-0

                  rounded-full

                  bg-[#159957]
                "
              />


              {/* LIVE */}

              <span
                className="
                  employee-dashboard-live-text

                  font-semibold

                  text-[#007A4D]
                "
              >
                LIVE
              </span>


              <span
                className="
                  shrink-0

                  text-[#98A2B3]
                "
              >
                •
              </span>


              {/* Date */}

              <span
                className="
                  employee-dashboard-live-text

                  text-[#0070C0]
                "
              >
                {formattedDate}
              </span>


              <span
                className="
                  shrink-0

                  text-[#98A2B3]
                "
              >
                •
              </span>


              {/* Time */}

              <span
                className="
                  employee-dashboard-live-text

                  text-[#0070C0]
                "
              >
                {formattedTime} IST
              </span>

            </div>

          </div>


          {/* ================================================================
              API LOADING
          ================================================================ */}

          {loading ? (

            <DashboardLoader />

          ) : (

            <>

              {/* ============================================================
                  EMPLOYEE STATISTICS
              ============================================================ */}

              <div
                className="
                  employee-dashboard-content

                  w-full
                  min-w-0
                "
              >

                <EmployeeStatsSection />

              </div>


              {/* ============================================================
                  DAILY ACTIVITY + 30-DAY HOURS TREND
              ============================================================ */}

              <div
                className="
                  employee-dashboard-grid
                  employee-dashboard-two-column

                  mt-[14px]
                "
              >

                {/* Daily Activity */}

                <div
                  className="
                    employee-dashboard-grid-item
                  "
                >

                  <div
                    className="
                      employee-dashboard-chart-container
                    "
                  >

                    <ActivityTimeline />

                  </div>

                </div>


                {/* 30-Day Trend */}

                <div
                  className="
                    employee-dashboard-grid-item
                  "
                >

                  <div
                    className="
                      employee-dashboard-chart-container
                    "
                  >

                    <MonthlyHoursTrend
                      data={
                        trend30Day
                      }
                    />

                  </div>

                </div>

              </div>


              {/* ============================================================
                  WEEKLY HOURS + MONTHLY PRODUCTIVITY
              ============================================================ */}

              <div
                className="
                  employee-dashboard-grid
                  employee-dashboard-weekly-grid

                  mt-[14px]
                "
              >

                {/* Weekly Hours */}

                <div
                  className="
                    employee-dashboard-grid-item
                  "
                >

                  <div
                    className="
                      employee-dashboard-chart-container
                    "
                  >

                    <WeeklyHoursChart />

                  </div>

                </div>


                {/* Monthly Productivity */}

                <div
                  className="
                    employee-dashboard-grid-item
                  "
                >

                  <div
                    className="
                      employee-dashboard-chart-container
                    "
                  >

                    <MonthlyProductivity />

                  </div>

                </div>

              </div>


              {/* ============================================================
                  TOP APPLICATIONS + OVERVIEW
              ============================================================ */}

              <div
                className="
                  employee-dashboard-grid
                  employee-dashboard-app-grid

                  mt-[14px]
                "
              >

                {/* Top Applications */}

                <div
                  className="
                    employee-dashboard-grid-item
                  "
                >

                  <div
                    className="
                      employee-dashboard-chart-container
                    "
                  >

                    <TopApplications />

                  </div>

                </div>


                {/* Overview */}

                <div
                  className="
                    employee-dashboard-grid-item
                  "
                >

                  <div
                    className="
                      employee-dashboard-chart-container
                    "
                  >

                    <OverviewCard />

                  </div>

                </div>

              </div>


              {/* ============================================================
                  FOOTER
              ============================================================ */}

              <div
                className="
                  mt-[14px]

                  flex
                  min-w-0
                  w-full

                  flex-wrap
                  items-center
                  justify-between

                  gap-[8px]

                  border-t
                  border-[#E4E7EC]

                  pt-[10px]
                "
              >

                <p
                  className="
                    whitespace-nowrap

                    text-[11px]

                    text-[#667085]
                  "
                >
                  © 2025, Sagility.
                </p>


                <p
                  className="
                    whitespace-nowrap

                    text-[11px]

                    text-[#98A2B3]
                  "
                >
                  Powered by EAM.
                </p>

              </div>

            </>

          )}

        </div>

      </div>

    </>
  );
};


export default EmployeeDashboard;